// Axios client with access-token injection and refresh-on-401 logic.
// A single queue serializes concurrent refreshes so a burst of parallel
// requests doesn't spawn N refresh calls.

import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { storage } from './storage';
import type { ApiError, LoginResponse } from '@/types/api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080';

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// Attach Authorization header if a token is present.
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const access = storage.getAccess();
  if (access && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
});

// --- refresh pipeline ----------------------------------------------------

let refreshInFlight: Promise<string> | null = null;

/**
 * Single-flight token refresh shared by BOTH transports (axios interceptor
 * and the SSE chat path). Two parallel refreshes with the same rotating
 * refresh token make the loser's token invalid → forced logout mid-session.
 * Every caller must go through here so only one POST /auth/refresh is ever
 * in flight.
 */
export function refreshAccessShared(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doRefresh(): Promise<string> {
  const refresh = storage.getRefresh();
  if (!refresh) throw new Error('no refresh token');
  const resp = await axios.post<LoginResponse>(
    `${BASE_URL}/api/v1/auth/refresh`,
    { refresh_token: refresh },
    { headers: { 'Content-Type': 'application/json' } },
  );
  storage.setTokens(resp.data.access_token, resp.data.refresh_token);
  storage.setUser(resp.data.user);
  return resp.data.access_token;
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError<ApiError>) => {
    const original = err.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (err.response?.status === 401 && original && !original._retry) {
      const code = err.response.data?.error;
      // Only try refresh for expired access tokens — not e.g. invalid_credentials.
      if (code === 'expired_token' || code === 'invalid_token' || code === 'unauthorized') {
        original._retry = true;
        try {
          const newAccess = await refreshAccessShared();
          original.headers.Authorization = `Bearer ${newAccess}`;
          return api.request(original);
        } catch (refreshErr) {
          storage.clear();
          // Let the UI observe the failure; router will redirect to /login.
          window.dispatchEvent(new CustomEvent('auth:expired'));
          return Promise.reject(refreshErr);
        }
      }
    }
    return Promise.reject(err);
  },
);

// --- helpers -------------------------------------------------------------

/** Extract a user-facing message from an axios error. */
export function extractErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiError | undefined;
    if (data?.message) return data.message;
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export { BASE_URL };
